<template>
    <div>
        <DListGroup>
            <DListGroupItem class="d-flex justify-content-between align-items-center group-header">
                <div class="div-product">
                    {{ __('customers.contracts.create_contract_tree.product') }}
                </div>
                <div class="div-price">
                    {{ __('customers.contracts.create_contract_tree.price') }}
                </div>
                <div class="div-runtime">
                    {{ __('customers.contracts.create_contract_tree.runtime') }}
                </div>
                <div class="div-technical-details">
                    {{ __('customers.contracts.create_contract_tree.technical_details') }}
                </div>
                <div class="div-actions" />
            </DListGroupItem>
            <VueNestable
                v-model="contracts"
                :max-depth="contractMaxNestingLevel"
                key-prop="key"
                children-prop="children"
            >
                <template #default="{ item }">
                    <DListGroupItem
                        class="d-flex justify-content-between align-items-center group-body"
                    >
                        <div
                            class="div-product"
                        >
                            <div
                                class="product-body"
                                :class="productErrorsExits(item.key) ? 'border-error' : ''"
                                @click="setProductInfo(item)"
                            >
                                <div>
                                    {{ quantityFieldValue(item.config_values) }} x
                                </div>
                                <div class="meta">
                                    <div class="product-group-name">
                                        {{ fieldProductGroupNameValue(item.product_group_id) }}
                                    </div>
                                    <div class="product-name">
                                        {{ fieldValue(item.product_name) }}
                                    </div>
                                </div>
                                <div>
                                    <span v-if="item.start_date">
                                        {{ item.start_date }}
                                    </span>
                                    <span v-else>
                                        {{ fieldValue(item.start_date) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div class="div-price">
                            <div
                                class="price-body"
                                :class="[item.product_id ? '' : 'not-allowed', priceErrorsExits(item.key) ? 'border-error' : '']"
                                @click="item.product_id ? setPriceInfo(item) : ''"
                            >
                                <div class="meta">
                                    <div class="price">
                                        <span v-if="item.price">
                                            {{ `${roundPrice(item.price)} / ${billingCycle(item.billing_cycle)}` }}
                                        </span>
                                        <span v-else>
                                            {{ fieldValue(item.price) }}
                                        </span>
                                    </div>
                                    <div class="setup-fee">
                                        <span v-if="item.setup_fee">
                                            {{ roundSetup(item.setup_fee) }}
                                        </span>
                                        <span v-else>
                                            {{ fieldValue(item.setup_fee) }}
                                        </span>
                                    </div>
                                </div>
                                <div class="meta">
                                    <div class="price">
                                        {{ fieldNewPriceValue(item) }}
                                    </div>
                                    <div class="setup-fee">
                                        {{ fieldNewSetupValue(item) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="div-runtime">
                            <div
                                class="runtime-body"
                                :class="[item.product_id ? '' : 'not-allowed', runtimeErrorsExits(item.key) ? 'border-error' : '']"
                                @click="item.product_id && item.one_time_plan_selected === false ? setRuntimeInfo(item) : ''"
                            >
                                <div class="meta">
                                    <div class="notice">
                                        {{ fieldNoticeUntilValue(item) }}
                                    </div>
                                    <div
                                        v-if="fieldNoticeUntilDateValue(item) === null"
                                        class="date"
                                    >
                                        &nbsp;
                                    </div>
                                    <div
                                        v-else
                                        class="date"
                                    >
                                        {{ fieldNoticeUntilDateValue(item) }}
                                    </div>
                                </div>
                                <div class="meta">
                                    <div class="notice">
                                        {{ fieldNoticeAfterValue(item) }}
                                    </div>
                                    <div class="date">
                                        {{ fieldNoticeAfterDateValue(item) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="div-technical-details">
                            <div
                                class="technical-details-body"
                                :class="[item.product_id ? '' : 'not-allowed', technicalDetailsErrorsExits(item.key) ? 'border-error' : '']"
                                @click="item.product_id ? setTechnicalDetailsInfo(item) : ''"
                            >
                                <div class="meta">
                                    <div class="domain">
                                        {{ fieldDomainNameValue(item.config_values) }}
                                    </div>
                                    <div class="hostname">
                                        {{ fieldTechnicalDetailsValue(item.config_values) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="div-actions">
                            <div class="actions-body">
                                <!-- Handler -->
                                <VueNestableHandle :item="item">
                                    <div class="meta">
                                        <div>
                                            <DButton
                                                icon="drag-variant"
                                                min-height50
                                                drag-and-drop-btn
                                            />
                                        </div>
                                        <div />
                                    </div>
                                </VueNestableHandle>
                                <div class="meta">
                                    <div>
                                        <DButton
                                            icon="trash-can-outline"
                                            min-height50
                                            :disabled="isOnlyContract(item)"
                                            @click="deleteRow(item, contracts)"
                                        />
                                    </div>
                                    <div />
                                </div>
                            </div>
                        </div>
                    </DListGroupItem>
                </template>
            </VueNestable>
        </DListGroup>
        <DFabs>
            <DFab
                :color="'secondary'"
                icon="plus"
                :label="__('customers.contracts.add_line')"
                @click="addNewRow()"
            />
            <DFab
                :color="'secondary'"
                icon="plus"
                :label="__('customers.contracts.load_product_package')"
                @click="showProductPackageModal = true"
            />
            <DFab
                id="btn-save"
                icon="content-save"
                :label="__('customers.contracts.create_contract_tree.create_contract_tree')"
                @click="storeContracts()"
            />
        </DFabs>
        <ProductModal
            v-if="showProductModal"
            :show-modal="showProductModal"
            :contract="selectedContract"
            :customer="customer"
            :errors="productErrors"
            @submitProduct="setRowForProduct"
            @oneTimePlanSelected="setOneTimePlanSelected"
            @close-modal="showProductModal = false; selectedContract = null"
        />
        <TechnicalDetailsModal
            v-if="showTechnicalDetailsModal"
            :show-modal="showTechnicalDetailsModal"
            :contract="selectedContract"
            :errors="technicalDetailsErrors"
            @submitTechnicalDetails="setRowForTechnicalDetails"
            @close-modal="showTechnicalDetailsModal = false; selectedContract = null"
        />
        <RuntimeModal
            v-if="showRuntimeModal"
            :show-modal="showRuntimeModal"
            :contract="selectedContract"
            :contract-term-units="contractTermUnits"
            :errors="runtimeErrors"
            @submitRuntime="setRowForRuntime"
            @close-modal="showRuntimeModal = false; selectedContract = null"
        />
        <PriceModal
            v-if="showPriceModal"
            :show-modal="showPriceModal"
            :contract="selectedContract"
            :billing-cycles="billingCycles"
            :discount-types="discountTypes"
            :errors="priceErrors"
            @submitPrice="setRowForPrice"
            @close-modal="showPriceModal = false; selectedContract = null"
        />
        <ProductPackageModal
            v-if="showProductPackageModal"
            :customer="customer"
            :show-modal="showProductPackageModal"
            :product-packages="productPackages"
            @load-package="loadPackage"
            @close-modal="showProductPackageModal = false"
        />
    </div>
</template>

<script>
import { VueNestable, VueNestableHandle } from '@sparkz-community/vue3-nestable'
import ProductModal from '~@/Components/Customers/Contracts/Modals/ContractTree/Product'
import billingCycle from '~@/Mixins/billingCycle'
import TechnicalDetailsModal from '~@/Components/Customers/Contracts/Modals/ContractTree/TechnicalDetails'
import RuntimeModal from '~@/Components/Customers/Contracts/Modals/ContractTree/Runtime'
import PriceModal from '~@/Components/Customers/Contracts/Modals/ContractTree/Price'
import ProductPackageModal from '~@/Components/Customers/Contracts/Modals/ContractTree/ProductPackage'
import priceFormat from '~@/Mixins/priceFormat'

export default {
    name: 'CreateContractTree',
    components: { ProductPackageModal, PriceModal, RuntimeModal, TechnicalDetailsModal, ProductModal, VueNestable, VueNestableHandle },
    mixins: [
        billingCycle,
        priceFormat
    ],
    props: {
        customer: {
            type: Object,
            default () { return {} }
        },
        billingCycles: {
            type: Array,
            default () { return [] }
        },
        contractTermUnits: {
            type: Array,
            default () { return [] }
        },
        discountTypes: {
            type: Array,
            default () { return [] }
        },
        productGroups: {
            type: Array,
            default () { return [] }
        },
        productPackages: {
            type: Array,
            default () { return [] }
        },
        errors: {
            type: Object,
            default: () => { return {} }
        },
        contractMaxNestingLevel: {
            type: Number,
            default: () => { return 3 }
        }
    },
    data () {
        return {
            fakeKey: 0,
            contracts: [this.getNewRow()],
            showProductModal: false,
            showTechnicalDetailsModal: false,
            showRuntimeModal: false,
            showPriceModal: false,
            selectedContract: null,
            productErrors: {},
            priceErrors: {},
            runtimeErrors: {},
            technicalDetailsErrors: {},
            showProductPackageModal: false
        }
    },
    methods: {
        productErrorsExits (key) {
            return typeof this.errors[`contracts.${key}.product_id`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.product_plan_id`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.start_date`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.notes_invoice`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.notes_internal`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.config_values.quantity.value`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.up_and_downgrade_only_confirmed`] !== 'undefined'
        },
        getProductErrors (key) {
            this.productErrors = {}

            if (typeof this.errors[`contracts.${key}.product_id`] !== 'undefined') {
                this.productErrors.product_id = this.errors[`contracts.${key}.product_id`]
            }

            if (typeof this.errors[`contracts.${key}.start_date`] !== 'undefined') {
                this.productErrors.start_date = this.errors[`contracts.${key}.start_date`]
            }

            if (typeof this.errors[`contracts.${key}.notes_invoice`] !== 'undefined') {
                this.productErrors.notes_invoice = this.errors[`contracts.${key}.notes_invoice`]
            }

            if (typeof this.errors[`contracts.${key}.notes_internal`] !== 'undefined') {
                this.productErrors.notes_internal = this.errors[`contracts.${key}.notes_internal`]
            }

            if (typeof this.errors[`contracts.${key}.config_values.quantity.value`] !== 'undefined') {
                this.productErrors.quantity = this.errors[`contracts.${key}.config_values.quantity.value`]
            }

            if (typeof this.errors[`contracts.${key}.up_and_downgrade_only_confirmed`] !== 'undefined') {
                this.productErrors.up_and_downgrade_only_confirmed = this.errors[`contracts.${key}.up_and_downgrade_only_confirmed`]
            }
        },
        technicalDetailsErrorsExits (key) {
            const contract = this.findNested(this.contracts, key)

            if (contract !== null) {
                for (const config of contract.config_values) {
                    if (typeof this.errors[`contracts.${key}.config_values.${config.name}.value`] !== 'undefined') {
                        return true
                    }
                }
            }

            return false
        },
        getTechnicalDetailsErrors (key) {
            this.technicalDetailsErrors = {}
            const contract = this.findNested(this.contracts, key)

            if (contract !== null) {
                for (const config of contract.config_values) {
                    if (typeof this.errors[`contracts.${key}.config_values.${config.name}.value`] !== 'undefined') {
                        this.technicalDetailsErrors[`${config.name}`] = this.errors[`contracts.${key}.config_values.${config.name}.value`]
                    }
                }
            }
        },
        priceErrorsExits (key) {
            return typeof this.errors[`contracts.${key}.billing_cycle`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.price`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.setup_fee`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.new_price_price`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.new_price_price_with_discount`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.new_price_discount_name`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.new_price_discount_type`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.new_price_discount_value`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.new_price_months`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.new_price_months_with_discount`] !== 'undefined'
        },
        getPriceErrors (key) {
            this.priceErrors = {}

            if (typeof this.errors[`contracts.${key}.billing_cycle`] !== 'undefined') {
                this.priceErrors.billing_cycle = this.errors[`contracts.${key}.billing_cycle`]
            }

            if (typeof this.errors[`contracts.${key}.price`] !== 'undefined') {
                this.priceErrors.price = this.errors[`contracts.${key}.price`]
            }

            if (typeof this.errors[`contracts.${key}.setup_fee`] !== 'undefined') {
                this.priceErrors.setup_fee = this.errors[`contracts.${key}.setup_fee`]
            }

            if (typeof this.errors[`contracts.${key}.new_price_price`] !== 'undefined') {
                this.priceErrors.new_price_price = this.errors[`contracts.${key}.new_price_price`]
            }

            if (typeof this.errors[`contracts.${key}.new_price_price_with_discount`] !== 'undefined') {
                this.priceErrors.new_price_price_with_discount = this.errors[`contracts.${key}.new_price_price_with_discount`]
            }

            if (typeof this.errors[`contracts.${key}.new_price_discount_name`] !== 'undefined') {
                this.priceErrors.new_price_discount_name = this.errors[`contracts.${key}.new_price_discount_name`]
            }

            if (typeof this.errors[`contracts.${key}.new_price_discount_type`] !== 'undefined') {
                this.priceErrors.new_price_discount_type = this.errors[`contracts.${key}.new_price_discount_type`]
            }

            if (typeof this.errors[`contracts.${key}.new_price_discount_value`] !== 'undefined') {
                this.priceErrors.new_price_discount_value = this.errors[`contracts.${key}.new_price_discount_value`]
            }

            if (typeof this.errors[`contracts.${key}.new_price_months`] !== 'undefined') {
                this.priceErrors.new_price_months = this.errors[`contracts.${key}.new_price_months`]
            }

            if (typeof this.errors[`contracts.${key}.new_price_months_with_discount`] !== 'undefined') {
                this.priceErrors.new_price_months_with_discount = this.errors[`contracts.${key}.new_price_months_with_discount`]
            }
        },
        runtimeErrorsExits (key) {
            return typeof this.errors[`contracts.${key}.contract_term`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.contract_term_unit`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.notice_period`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.notice_period_unit`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.new_runtime_contract_term`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.new_runtime_contract_term_unit`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.new_runtime_notice_period`] !== 'undefined' ||
                typeof this.errors[`contracts.${key}.new_runtime_notice_period_unit`] !== 'undefined'
        },
        getRuntimeErrors (key) {
            this.runtimeErrors = {}

            if (typeof this.errors[`contracts.${key}.contract_term`] !== 'undefined') {
                this.runtimeErrors.contract_term = this.errors[`contracts.${key}.contract_term`]
            }

            if (typeof this.errors[`contracts.${key}.contract_term_unit`] !== 'undefined') {
                this.runtimeErrors.contract_term_unit = this.errors[`contracts.${key}.contract_term_unit`]
            }

            if (typeof this.errors[`contracts.${key}.notice_period`] !== 'undefined') {
                this.runtimeErrors.notice_period = this.errors[`contracts.${key}.notice_period`]
            }

            if (typeof this.errors[`contracts.${key}.notice_period_unit`] !== 'undefined') {
                this.runtimeErrors.notice_period_unit = this.errors[`contracts.${key}.notice_period_unit`]
            }

            if (typeof this.errors[`contracts.${key}.new_runtime_contract_term`] !== 'undefined') {
                this.runtimeErrors.new_runtime_contract_term = this.errors[`contracts.${key}.new_runtime_contract_term`]
            }

            if (typeof this.errors[`contracts.${key}.new_runtime_contract_term_unit`] !== 'undefined') {
                this.runtimeErrors.new_runtime_contract_term_unit = this.errors[`contracts.${key}.new_runtime_contract_term_unit`]
            }

            if (typeof this.errors[`contracts.${key}.new_runtime_notice_period`] !== 'undefined') {
                this.runtimeErrors.new_runtime_notice_period = this.errors[`contracts.${key}.new_runtime_notice_period`]
            }

            if (typeof this.errors[`contracts.${key}.new_runtime_notice_period_unit`] !== 'undefined') {
                this.runtimeErrors.new_runtime_notice_period_unit = this.errors[`contracts.${key}.new_runtime_notice_period_unit`]
            }
        },
        getNewRow () {
            return {
                key: 0,
                product_id: null,
                product_name: null,
                product_group_id: null,
                product_plan_id: null,
                product_retired: false,
                customer_id: null,
                start_date: this.getTodayFormatted(),
                notes_invoice: null,
                notes_internal: null,
                parent_contract_id: null,
                config_values: [],
                price: null,
                billing_cycle: null,
                setup_fee: null,
                notice_period: null,
                contract_term: null,
                contract_term_unit: null,
                notice_period_unit: null,
                terms_and_conditions_id: null,
                children: [],
                available_product_plans: [],
                new_price_with_discount: false,
                new_price_without_discount: false,
                new_price_start_date: null,
                new_price_price: null,
                new_price_price_with_discount: null,
                new_price_billing_cycle: null,
                new_price_discount_name: null,
                new_price_discount_type: null,
                new_price_discount_value: null,
                new_price_nett_price: null,
                new_price_months: null,
                new_price_months_with_discount: null,
                new_runtime_notice_period: null,
                new_runtime_contract_term: null,
                new_runtime_contract_term_unit: null,
                new_runtime_notice_period_unit: null,
                fulfillment_module: null,
                valid_from_month_required: false,
                up_and_downgrade_only: false,
                up_and_downgrade_only_confirmed: false
            }
        },
        roundPrice (value) {
            return this.priceFormat(value, 2, this.customer.market_region_id)
        },
        roundSetup (value) {
            return parseFloat(value).toFixed(2) + ' ' + this.__('customers.contracts.create_contract_tree.setup')
        },
        deleteRow (item, contracts, parentContract = null) {
            for (const contractItem of contracts) {
                if (contractItem.key === item.key) {
                    // If contract have parent contract and children, add children to parent contract
                    if (parentContract !== null && contractItem.children.length > 0) {
                        for (const child of contractItem.children) {
                            parentContract.children.push(child)
                        }
                    }

                    contracts.splice(contracts.indexOf(item), 1)

                    return
                }

                if (contractItem.children.length > 0) {
                    this.deleteRow(item, contractItem.children, contractItem)
                }
            }
        },
        addNewRow () {
            const newEmptyRow = this.getNewRow()
            newEmptyRow.key = ++this.fakeKey
            this.contracts.push(newEmptyRow)
        },
        storeContracts () {
            if (this.contracts.length === 0) {
                return
            }

            const form = this.$inertia.form({ contracts: this.flattenContracts(this.contracts) })

            form.post(this.$route('customers.customers.contracts.storeContractTree', {
                customer: this.customer.id
            }))
        },
        flattenContracts (contracts, parentKey = null) {
            if (contracts.length === 0) {
                return {}
            }

            let result = {}

            for (const contract of contracts) {
                const tmpContract = JSON.parse(JSON.stringify(contract))
                delete tmpContract.children

                tmpContract.parent_key = parentKey
                const configOptions = tmpContract.config_values
                tmpContract.config_values = {}

                for (const configValue of configOptions) {
                    tmpContract.config_values[configValue.name] = configValue
                }

                result[contract.key] = tmpContract
                result = { ...result, ...this.flattenContracts(contract.children, contract.key) }
            }

            return result
        },
        isOnlyContract (item) {
            return this.contracts.length === 1 && this.contracts[0].key === item.key
        },
        fieldValue (value) {
            return value || this.__('customers.contracts.create_contract_tree.no_data')
        },
        fieldProductGroupNameValue (productGroupId) {
            if (productGroupId === null) {
                return this.__('customers.contracts.create_contract_tree.no_data')
            }

            const productGroup = this.productGroups.find(group => Number(group.id) === Number(productGroupId))
            if (productGroup !== undefined) {
                return productGroup.name + ' (' + productGroup.id + ')'
            }

            return this.__('customers.contracts.create_contract_tree.no_data')
        },
        fieldNewPriceValue (item) {
            if (item.new_price_without_discount) {
                return this.priceFormatForDisplay(item.new_price_nett_price, 2, this.customer.market_region_id)
            }

            if (item.new_price_with_discount || item.new_price_discount_value !== null) {
                if (item.new_price_discount_type === 'fixed') {
                    return '-' + this.priceFormatForDisplay(item.new_price_discount_value, 2, this.customer.market_region_id)
                }
                if (item.new_price_discount_type === 'percentage') {
                    return item.new_price_discount_value + '%' +
                        ' (' + this.priceFormatForDisplay(this.parseToFloat(item.new_price_price_with_discount - item.new_price_nett_price), 2, this.customer.market_region_id) + ')'
                }
            }

            return this.__('customers.contracts.create_contract_tree.no_discount')
        },
        fieldNewSetupValue (item) {
            if (item.new_price_without_discount) {
                return this.__('customers.contracts.create_contract_tree.after') + ' ' + new Date(item.new_price_start_date).toLocaleDateString('de')
            }
            if (item.new_price_with_discount || item.new_price_discount_value !== null) {
                if (item.new_price_discount_type === 'fixed') {
                    const date = item.valid_from_month_required === true
                        ? ' ' +
                        this.__('customers.contracts.create_contract_tree.until') +
                        ' ' + new Date(item.new_price_start_date).toLocaleDateString('de')
                        : ''

                    return this.parseToFloat(Number(item.new_price_nett_price)) + date
                }

                if (item.new_price_discount_type === 'percentage') {
                    const date = item.valid_from_month_required === true
                        ? ' ' +
                        this.__('customers.contracts.create_contract_tree.until') +
                        ' ' + new Date(item.new_price_start_date).toLocaleDateString('de')
                        : ''

                    return this.parseToFloat(Number(item.new_price_nett_price)) + date
                }
            }

            return this.__('customers.contracts.create_contract_tree.no_discount')
        },
        fieldDomainNameValue (configValues) {
            if (configValues.length === 0) {
                return this.__('customers.contracts.create_contract_tree.no_data')
            }

            let domainName = null
            let firstConfigName = null

            for (const config of configValues) {
                // Set first config name from array
                if (firstConfigName === null && config.value !== null && config.value !== '') {
                    firstConfigName = config.display_value !== null ? config.display_value : config.value
                }

                if (config.name === 'domain' && config.value !== null && config.value !== '') {
                    domainName = config.value !== null && config.value !== ''
                        ? config.value
                        : this.__('customers.contracts.create_contract_tree.no_data')
                }
            }

            if (domainName !== null) {
                return domainName
            }

            if (firstConfigName !== null) {
                return firstConfigName
            }

            return this.__('customers.contracts.create_contract_tree.no_data')
        },
        fieldTechnicalDetailsValue (configValues) {
            if (configValues.length === 0) {
                return this.__('customers.contracts.create_contract_tree.no_data')
            }

            let domainNameExist = false
            const configValuesWithoutDomain = []

            for (const config of configValues) {
                if (config.name !== 'domain' && config.value !== null && config.value !== '') {
                    configValuesWithoutDomain.push(config.display_value !== null ? config.display_value : config.value)
                }
                if (config.name === 'domain' && config.value !== null && config.value !== '') {
                    domainNameExist = true
                }
            }

            if (domainNameExist === true && configValues.length > 0 && configValuesWithoutDomain.length > 0) {
                if (configValuesWithoutDomain.length === 1) {
                    return configValuesWithoutDomain[0]
                }

                return configValuesWithoutDomain.join(', ')
            }

            if (domainNameExist === false && configValues.length > 0 && configValuesWithoutDomain.length > 0) {
                // Remove first element from array and then create string comma separated
                const configValuesWithoutDomainWithoutFirstElement = configValuesWithoutDomain.slice(1)
                if (configValuesWithoutDomainWithoutFirstElement.length === 0) {
                    return this.__('customers.contracts.create_contract_tree.no_data')
                }

                if (configValuesWithoutDomainWithoutFirstElement.length > 1) {
                    return configValuesWithoutDomainWithoutFirstElement.join(', ')
                }

                return configValuesWithoutDomainWithoutFirstElement[0]
            }

            return this.__('customers.contracts.create_contract_tree.no_data')
        },
        fieldNoticeUntilValue (item) {
            if (item.contract_term === null ||
                item.contract_term === '' ||
                item.contract_term_unit === null ||
                item.contract_term_unit === '' ||
                item.notice_period === null ||
                item.notice_period === '' ||
                item.notice_period_unit === null ||
                item.notice_period_unit === ''
            ) {
                return this.__('customers.contracts.create_contract_tree.no_data')
            }

            return item.contract_term + item.contract_term_unit.toLowerCase() +
                ', ' +
                item.notice_period + item.notice_period_unit.toLowerCase()
        },
        fieldNoticeUntilDateValue (item) {
            if (item.contract_term === null ||
                item.contract_term === '' ||
                item.contract_term_unit === null ||
                item.contract_term_unit === '' ||
                item.start_date === null
            ) {
                return this.__('customers.contracts.create_contract_tree.no_data')
            }

            if (item.new_runtime_contract_term === null ||
                item.new_runtime_contract_term === '' ||
                item.new_runtime_contract_term_unit === null ||
                item.new_runtime_contract_term_unit === '' ||
                item.new_runtime_notice_period === null ||
                item.new_runtime_notice_period === '' ||
                item.new_runtime_notice_period_unit === null ||
                item.new_runtime_notice_period_unit === ''
            ) {
                return null
            }

            return this.__('customers.contracts.create_contract_tree.until') + ' ' + this.fieldDateValue(item.start_date, item.contract_term, item.contract_term_unit)
        },
        fieldNoticeAfterValue (item) {
            if (item.new_runtime_contract_term === null ||
                item.new_runtime_contract_term === '' ||
                item.new_runtime_contract_term_unit === null ||
                item.new_runtime_contract_term_unit === '' ||
                item.new_runtime_notice_period === null ||
                item.new_runtime_notice_period === '' ||
                item.new_runtime_notice_period_unit === null ||
                item.new_runtime_notice_period_unit === ''
            ) {
                return this.__('customers.contracts.create_contract_tree.no_data')
            }

            return item.new_runtime_contract_term + item.new_runtime_contract_term_unit.toLowerCase() +
                ', ' +
                item.new_runtime_notice_period + item.new_runtime_notice_period_unit.toLowerCase()
        },
        fieldNoticeAfterDateValue (item) {
            if (item.new_runtime_contract_term === null || item.new_runtime_contract_term === '' ||
                item.new_runtime_contract_term_unit === null || item.new_runtime_contract_term_unit === '' ||
                item.start_date === null
            ) {
                return this.__('customers.contracts.create_contract_tree.no_data')
            }

            return this.__('customers.contracts.create_contract_tree.after') + ' ' + this.fieldDateValue(item.start_date, item.contract_term, item.contract_term_unit)
        },
        fieldDateValue (date, increaseNumber, unit = 'M') {
            if (date === null || increaseNumber === null) {
                return this.__('customers.contracts.create_contract_tree.no_data')
            }

            const newDate = new Date(date)

            if (unit === 'M') {
                const newDate = new Date(date)
                const currentDate = newDate.getDate()
                newDate.setMonth(newDate.getMonth() + Number(increaseNumber))
                if (newDate.getDate() !== currentDate) {
                    newDate.setDate(0)
                }
                return newDate.toLocaleDateString('de')
            }

            if (unit === 'D') {
                return new Date(newDate.setDate(newDate.getDate() + Number(increaseNumber))).toLocaleDateString('de')
            }

            return this.__('customers.contracts.create_contract_tree.no_data')
        },
        quantityFieldValue (configValues) {
            for (const config of configValues) {
                if (config.name === 'quantity') {
                    return config.value
                }
            }

            return 1
        },
        setProductInfo (item) {
            this.getProductErrors(item.key)
            this.selectedContract = item
            this.showProductModal = true
        },
        setTechnicalDetailsInfo (item) {
            this.getTechnicalDetailsErrors(item.key)
            this.selectedContract = item
            this.showTechnicalDetailsModal = true
        },
        setRuntimeInfo (item) {
            this.getRuntimeErrors(item.key)
            this.selectedContract = item
            this.showRuntimeModal = true
        },
        setPriceInfo (item) {
            this.getPriceErrors(item.key)
            this.selectedContract = item
            this.showPriceModal = true
        },
        findNested (contracts, key) {
            for (const contract of contracts) {
                if (contract.key === key) {
                    return contract
                }

                if (contract.children.length > 0) {
                    const match = this.findNested(contract.children, key)

                    if (match !== null) {
                        return match
                    }
                }
            }

            return null
        },
        setRowForProduct (form) {
            const contract = this.findNested(this.contracts, form.key)

            if (contract !== null) {
                contract.product_id = form.product_id
                contract.product_name = form.product_name
                contract.product_group_id = form.product_group_id
                contract.product_group_name = form.product_group_name
                contract.product_plan_id = form.product_plan_id
                contract.product_retired = form.product_retired
                contract.start_date = form.start_date !== '' ? form.start_date : null
                contract.notes_invoice = form.notes_invoice
                contract.notes_internal = form.notes_internal
                contract.config_values = form.config_values
                contract.price = form.price
                contract.billing_cycle = form.billing_cycle
                contract.setup_fee = form.setup_fee
                contract.notice_period = form.notice_period
                contract.contract_term = form.contract_term
                contract.contract_term_unit = form.contract_term_unit
                contract.notice_period_unit = form.notice_period_unit
                contract.available_product_plans = form.available_product_plans
                contract.new_price_with_discount = form.new_price_with_discount
                contract.new_price_without_discount = form.new_price_without_discount
                contract.new_price_price = form.new_price_price
                contract.new_price_price_with_discount = form.new_price_discount_value !== null ? form.price : null
                contract.new_price_billing_cycle = null
                contract.new_price_discount_name = form.new_price_discount_name
                contract.new_price_discount_value = form.new_price_discount_value
                contract.new_price_discount_type = form.new_price_discount_type
                contract.new_price_nett_price = form.new_price_discount_value !== null ? this.updatePrice(contract) : this.updateNewPrice(form.new_price_price)
                contract.new_price_start_date = form.start_date !== null && form.new_price_discount_value !== null ? this.setStartDate(contract.start_date, form.new_price_months_with_discount, form.valid_from_month_required) : this.setStartDate(contract.start_date, form.new_price_months, form.valid_from_month_required)
                contract.new_price_months = form.new_price_months
                contract.new_price_months_with_discount = form.new_price_months_with_discount
                contract.new_runtime_notice_period = null
                contract.new_runtime_contract_term = null
                contract.new_runtime_contract_term_unit = null
                contract.new_runtime_notice_period_unit = null
                contract.fulfillment_module = form.fulfillment_module
                contract.valid_from_month_required = form.valid_from_month_required
                contract.up_and_downgrade_only = form.up_and_downgrade_only
                contract.up_and_downgrade_only_confirmed = form.up_and_downgrade_only_confirmed
            }
        },
        setOneTimePlanSelected (form, value) {
            const contract = this.findNested(this.contracts, form.key)

            contract.one_time_plan_selected = value
        },
        setRowForTechnicalDetails (form) {
            const contract = this.findNested(this.contracts, form.key)

            if (contract !== null) {
                contract.config_values = form.config_values
            }
        },
        setRowForRuntime (form) {
            const contract = this.findNested(this.contracts, form.key)

            if (contract !== null) {
                contract.notice_period = form.notice_period
                contract.contract_term = form.contract_term
                contract.contract_term_unit = form.contract_term_unit
                contract.notice_period_unit = form.notice_period_unit
                contract.new_runtime_notice_period = form.new_runtime_notice_period
                contract.new_runtime_contract_term = form.new_runtime_contract_term
                contract.new_runtime_contract_term_unit = form.new_runtime_contract_term_unit
                contract.new_runtime_notice_period_unit = form.new_runtime_notice_period_unit
            }
        },
        setRowForPrice (form) {
            const contract = this.findNested(this.contracts, form.key)

            if (contract !== null) {
                contract.valid_from_month_required = form.valid_from_month_required
                contract.price = form.price
                contract.billing_cycle = form.billing_cycle
                contract.setup_fee = form.setup_fee
                contract.new_price_with_discount = form.new_price_with_discount
                contract.new_price_without_discount = form.new_price_without_discount
                contract.new_price_start_date = form.new_price_start_date
                contract.new_price_price = form.new_price_price
                contract.new_price_price_with_discount = form.new_price_price_with_discount
                contract.new_price_billing_cycle = form.billing_cycle
                contract.new_price_discount_name = form.new_price_discount_name
                contract.new_price_discount_type = form.new_price_discount_type
                contract.new_price_discount_value = form.new_price_discount_value
                contract.new_price_nett_price = form.new_price_nett_price
                contract.new_price_months = form.new_price_months
                contract.new_price_months_with_discount = form.new_price_months_with_discount
            }
        },
        loadPackage (data) {
            if (this.contracts.length === 1 && this.contracts[0].children.length === 0 && this.contracts[0].product_group_id === null) {
                this.contracts = []
            }

            data.contracts.forEach((contract) => {
                this.setValidFromMonthRequiredForContractAndChildren(contract)
                this.contracts.push(this.overrideKeysForContractAndChildren(contract))
            })
            this.showProductPackageModal = false
        },
        overrideKeysForContractAndChildren (contract) {
            contract.key = ++this.fakeKey

            if (contract.children.length > 0) {
                contract.children.map(child => this.overrideKeysForContractAndChildren(child))
            }

            return contract
        },
        setValidFromMonthRequiredForContractAndChildren (contract) {
            // Assuming the information send by the backend is always correct, the field `valid_from_month_required`
            // can default to `false` as it is always set correctly again by confirming the product or price modals.
            contract.valid_from_month_required = false
            contract.children.forEach((child) => this.setValidFromMonthRequiredForContractAndChildren(child))
        },
        parseToFloat (number) {
            return Number(number).toFixed(2)
        },
        getTodayFormatted () {
            const date = new Date()

            let day = date.getDate()
            let month = date.getMonth() + 1
            const year = date.getFullYear()

            if (month < 10) month = '0' + month
            if (day < 10) day = '0' + day

            return year + '-' + month + '-' + day
        },
        updatePrice (newPrice, taxRate = 0) {
            return this.parseToFloat((newPrice.price - this.calculateDiscount(newPrice)) * (1 + taxRate / 100))
        },
        updateNewPrice (newPrice) {
            if (newPrice === null) {
                return null
            }

            return this.parseToFloat(newPrice)
        },
        calculateDiscount (newPrice) {
            return newPrice.new_price_discount_type === 'fixed' ? newPrice.new_price_discount_value : (newPrice.new_price_discount_value / 100) * newPrice.price
        },
        setStartDate (date, months, validFromMonthRequired) {
            if (months === null || validFromMonthRequired === false) {
                return null
            }

            const newDate = new Date(date)
            const currentDate = newDate.getDate()
            newDate.setMonth(newDate.getMonth() + Number(months))
            if (newDate.getDate() !== currentDate) {
                newDate.setDate(0)
            }

            return this.getDateFormatted(newDate)
        },
        getDateFormatted (newDate) {
            const date = new Date(newDate)

            let day = date.getDate()
            let month = date.getMonth() + 1
            const year = date.getFullYear()

            if (month < 10) month = '0' + month
            if (day < 10) day = '0' + day

            return year + '-' + month + '-' + day
        }
    }
}
</script>

<style lang="scss">
/*
* Style for nestable
*/
.nestable {
    position: relative;
}
.nestable-rtl {
    direction: rtl;
}
.nestable .nestable-list {
    margin: 0;
    padding: 0 0 0 40px;
    list-style-type: none;
}
.nestable-rtl .nestable-list {
    padding: 0 40px 0 0;
}
.nestable > .nestable-list {
    padding: 0;
}
.nestable-item:first-child,
.nestable-item-copy:first-child {
    margin-top: 0;
}
.nestable-item {
    position: relative;
}
.nestable-item.is-dragging .nestable-list {
    pointer-events: none;
}
.nestable-item.is-dragging * {
    opacity: 0;
    filter: alpha(opacity=0);
}
.nestable-item.is-dragging:before {
    content: ' ';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(106, 127, 233, 0.274);
    border: 1px dashed rgb(73, 100, 241);
    -webkit-border-radius: 5px;
    border-radius: 5px;
}
.nestable-drag-layer {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 100;
    pointer-events: none;
}
.nestable-rtl .nestable-drag-layer {
    left: auto;
    right: 0;
}
.nestable-drag-layer > .nestable-list {
    position: absolute;
    top: 0;
    left: 0;
    padding: 0;
    background-color: rgba(106, 127, 233, 0.274);
}
.nestable-rtl .nestable-drag-layer > .nestable-list {
    padding: 0;
}
.nestable-handle {
    display: inline;
}
.group-header {
    border-bottom: 1px solid rgba(0,0,0,0.14);
    align-items: center;

    & > div {
        font-weight: bold;
    }
}
.group-body {
    border-bottom: 1px solid rgba(0,0,0,0.14);
    align-items: center;

    & > div {
        & > div {
            padding: 10px;
            background: var(--colorSecondary);
            border-radius: 5px;
            height: auto;
        }
    }
}
.div-product {
    width: 31%;
    padding-right: 10px;
}
.div-price {
    width: 20%;
    padding-right: 10px;
}
.div-runtime {
    width: 20%;
    padding-right: 10px;
}
.div-technical-details {
    width: 20%;
    padding-right: 10px;
}
.div-actions {
    width: 9%;
}
.product-body {
    padding: 15px;
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-gap: 7px;
    align-items: center;
    cursor: pointer;

    & > .meta {
        display: grid;
        grid-gap: 3px;
        padding-right: 5px;
        padding-left: 5px;
        border-left: 1px solid rgba(0,0,0,0.14);
        border-right: 1px solid rgba(0,0,0,0.14);

        & .product-group-name {
            font-weight: normal;
            font-size: 10px;
        }
        & .product-name {
            font-weight: bold;
            word-break: break-all;
            font-size: 11px;
        }
    }
}
.price-body {
    padding: 15px;
    display: grid;
    grid-template-columns: auto auto;
    grid-gap: 7px;
    align-items: center;
    cursor: pointer;

    & > .meta {
        display: grid;
        grid-gap: 3px;

        & .price {
            font-weight: bold;
            font-size: 11px;
        }
        & .setup-fee {
            font-weight: normal;
            font-size: 10px;
        }

        &:not(:last-child) {
            border-right: 1px solid rgba(0,0,0,0.14);
        }
    }
}
.runtime-body {
    padding: 15px;
    display: grid;
    grid-template-columns: auto auto;
    grid-gap: 7px;
    align-items: center;
    cursor: pointer;

    & > .meta {
        display: grid;
        grid-gap: 3px;

        & .notice {
            font-weight: bold;
            font-size: 11px;
        }
        & .date {
            font-weight: normal;
            font-size: 10px;
        }

        &:not(:last-child) {
            border-right: 1px solid rgba(0,0,0,0.14);
        }
    }
}
.technical-details-body {
    padding: 15px;
    display: grid;
    grid-template-columns: auto;
    align-items: center;
    cursor: pointer;

    & > .meta {
        display: grid;
        grid-gap: 3px;

        & .domain {
            font-weight: bold;
            font-size: 11px;
        }
        & .hostname {
            font-weight: normal;
            font-size: 10px;
        }
    }
}
.actions-body {
    display: grid;
    grid-template-columns: auto auto;
    grid-gap: 7px;
    align-items: center;
    padding: 0 !important;
    background: transparent !important;
}
.not-allowed {
    cursor: not-allowed;
}
.border-error {
    border: 1px solid var(--colorBorderError);
}
</style>
